/**
 * Digital Twin Page - Visualization
 * Attempts 3D visualization, falls back to 2D SVG if 3D fails
 * This addresses persistent react-three-fiber/drei compatibility issues
 */
import { useState, useEffect, useRef, Suspense, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plane, Building2, Cpu, AlertTriangle, RefreshCw, 
  Layers, Activity, ZoomIn, ZoomOut, RotateCw, Eye,
  HelpCircle, ChevronDown, ChevronUp, ExternalLink, Calendar, MapPin, User, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Severity colors
const SEVERITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  minor: '#22c55e',
  major: '#f97316',
  info: '#3b82f6'
};

// ============== SVG AIRCRAFT VIEW ==============
function AircraftSVG({ incidents, selectedIncident, onSelectIncident, showHotspots, newIncidentIds = [] }) {
  const svgRef = useRef(null);
  const zones = {
    cockpit: { x: 750, y: 200, label: 'Cockpit' },
    cabin_front: { x: 650, y: 200, label: 'Front Cabin' },
    cabin_mid: { x: 500, y: 200, label: 'Mid Cabin' },
    cabin_rear: { x: 350, y: 200, label: 'Rear Cabin' },
    wing_left: { x: 500, y: 100, label: 'Left Wing' },
    wing_right: { x: 500, y: 300, label: 'Right Wing' },
    engine_left: { x: 550, y: 80, label: 'Left Engine' },
    engine_right: { x: 550, y: 320, label: 'Right Engine' },
    tail: { x: 200, y: 200, label: 'Tail' },
    landing_gear: { x: 450, y: 200, label: 'Landing Gear' },
    cargo: { x: 400, y: 250, label: 'Cargo' },
  };

  const getStableOffset = (id, maxOffset) => {
    const hash = id ? id.charCodeAt(0) + id.charCodeAt(id.length - 1) : 0;
    return ((hash % 100) / 100 - 0.5) * maxOffset;
  };

  // Render markers directly into SVG DOM to bypass dev tooling span injection
  useEffect(() => {
    if (!svgRef.current || !showHotspots) return;
    
    // Remove existing markers
    const existingMarkers = svgRef.current.querySelectorAll('.incident-marker');
    existingMarkers.forEach(m => m.remove());
    
    // Add markers for each incident
    incidents.forEach((incident, index) => {
      const zone = zones[incident.location_zone] || zones.cabin_mid;
      const color = SEVERITY_COLORS[incident.severity] || SEVERITY_COLORS.info;
      const brightColor = color === '#3b82f6' ? '#60a5fa' : color;
      const isSelected = selectedIncident?.id === incident.id;
      const isNew = newIncidentIds.includes(incident.id);
      const offsetX = getStableOffset(incident.id, 30);
      const offsetY = getStableOffset(incident.id + 'y', 20);
      const cx = zone.x + offsetX;
      const cy = zone.y + offsetY;
      
      // Create marker group
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('class', 'incident-marker');
      g.style.cursor = 'pointer';
      g.onclick = () => onSelectIncident(incident);
      
      // Background circle (dark outline)
      const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      bgCircle.setAttribute('cx', cx);
      bgCircle.setAttribute('cy', cy);
      bgCircle.setAttribute('r', isSelected ? 20 : 16);
      bgCircle.setAttribute('fill', '#000000');
      bgCircle.setAttribute('opacity', '0.7');
      g.appendChild(bgCircle);
      
      // White circle
      const whiteCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      whiteCircle.setAttribute('cx', cx);
      whiteCircle.setAttribute('cy', cy);
      whiteCircle.setAttribute('r', isSelected ? 18 : 14);
      whiteCircle.setAttribute('fill', '#ffffff');
      g.appendChild(whiteCircle);
      
      // Main colored circle
      const mainCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      mainCircle.setAttribute('cx', cx);
      mainCircle.setAttribute('cy', cy);
      mainCircle.setAttribute('r', isSelected ? 15 : 11);
      mainCircle.setAttribute('fill', brightColor);
      if (isSelected) {
        mainCircle.setAttribute('stroke', '#ffffff');
        mainCircle.setAttribute('stroke-width', '2');
      }
      g.appendChild(mainCircle);
      
      // Incident number text
      const reportNum = incident.report_number?.replace('INC-', '') || String(index + 1);
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', cx);
      text.setAttribute('y', cy + 4);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', isSelected ? '10' : '8');
      text.setAttribute('font-weight', '700');
      text.setAttribute('fill', '#ffffff');
      text.textContent = reportNum.slice(-2); // Last 2 digits
      g.appendChild(text);
      
      svgRef.current.appendChild(g);
    });
  }, [incidents, selectedIncident, showHotspots, newIncidentIds, onSelectIncident]);

  return (
    <svg ref={svgRef} viewBox="0 0 900 400" className="w-full h-full bg-slate-950">
      <defs>
        <linearGradient id="fuselageGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="50%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>
        <linearGradient id="wingGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Grid */}
      <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
        <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#1e293b" strokeWidth="0.5" />
      </pattern>
      <rect width="900" height="400" fill="url(#grid)" />

      {/* Wings */}
      <ellipse cx="500" cy="200" rx="40" ry="150" fill="url(#wingGrad)" />
      
      {/* Fuselage */}
      <ellipse cx="500" cy="200" rx="350" ry="35" fill="url(#fuselageGrad)" />
      
      {/* Nose */}
      <ellipse cx="800" cy="200" rx="60" ry="25" fill="#64748b" />
      
      {/* Tail vertical */}
      <path d="M 150 200 L 150 120 L 200 160 L 200 200 Z" fill="#3b82f6" />
      
      {/* Tail horizontal */}
      <ellipse cx="160" cy="200" rx="15" ry="50" fill="#475569" />
      
      {/* Engines */}
      <ellipse cx="550" cy="80" rx="40" ry="15" fill="#374151" />
      <ellipse cx="550" cy="320" rx="40" ry="15" fill="#374151" />
      
      {/* Cockpit windows */}
      <rect x="770" y="190" width="30" height="20" rx="3" fill="#0f172a" />

      {/* Zone labels - static */}
      <text x="750" y="250" fontSize="10" fill="#64748b" textAnchor="middle">Cockpit</text>
      <text x="650" y="250" fontSize="10" fill="#64748b" textAnchor="middle">Front Cabin</text>
      <text x="500" y="250" fontSize="10" fill="#64748b" textAnchor="middle">Mid Cabin</text>
      <text x="350" y="250" fontSize="10" fill="#64748b" textAnchor="middle">Rear Cabin</text>
      <text x="500" y="60" fontSize="10" fill="#64748b" textAnchor="middle">Left Wing</text>
      <text x="500" y="360" fontSize="10" fill="#64748b" textAnchor="middle">Right Wing</text>
      <text x="550" y="50" fontSize="10" fill="#64748b" textAnchor="middle">Left Engine</text>
      <text x="550" y="370" fontSize="10" fill="#64748b" textAnchor="middle">Right Engine</text>
      <text x="200" y="250" fontSize="10" fill="#64748b" textAnchor="middle">Tail</text>
      <text x="450" y="250" fontSize="10" fill="#64748b" textAnchor="middle">Landing Gear</text>
      <text x="400" y="300" fontSize="10" fill="#64748b" textAnchor="middle">Cargo</text>

      {/* Incident markers are rendered via useEffect */}
    </svg>
  );
}

// ============== SVG FACILITY MAP ==============
function FacilitySVG({ incidents, selectedIncident, onSelectIncident, showHeatmap, newIncidentIds = [] }) {
  const svgRef = useRef(null);
  // Facility layout - shifted down to ensure visibility
  const locations = {
    atc: { x: 30, y: 30, w: 60, h: 50, label: 'ATC Tower', color: '#0ea5e9' },
    terminal: { x: 200, y: 25, w: 200, h: 60, label: 'Terminal Building', color: '#64748b' },
    fuel: { x: 520, y: 30, w: 70, h: 50, label: 'Fuel Depot', color: '#dc2626' },
    h1: { x: 30, y: 100, w: 90, h: 70, label: 'Hangar 1', color: '#475569' },
    apron: { x: 150, y: 105, w: 280, h: 60, label: 'Apron / Ramp', color: '#334155' },
    h2: { x: 480, y: 100, w: 90, h: 70, label: 'Hangar 2', color: '#475569' },
    taxiway: { x: 50, y: 190, w: 500, h: 25, label: 'Taxiway Alpha', color: '#52525b' },
    runway: { x: 30, y: 240, w: 540, h: 45, label: 'Runway 27L / 09R', color: '#27272a' },
  };

  const getLocationCenter = (loc) => {
    const l = locations[loc] || locations.apron;
    return { x: l.x + l.w / 2, y: l.y + l.h / 2 };
  };

  const getStableOffset = (id, maxOffset) => {
    const hash = id ? id.charCodeAt(0) + id.charCodeAt(id.length - 1) : 0;
    return ((hash % 100) / 100 - 0.5) * maxOffset;
  };

  // Render markers directly into SVG DOM to bypass dev tooling span injection
  useEffect(() => {
    if (!svgRef.current) return;
    
    // Remove existing markers
    const existingMarkers = svgRef.current.querySelectorAll('.facility-marker');
    existingMarkers.forEach(m => m.remove());
    
    // Add markers for each incident
    incidents.forEach((incident, index) => {
      const locKey = incident.location?.toLowerCase().replace(/ /g, '_') || 'apron';
      const matchingKey = Object.keys(locations).find(k => locKey.includes(k) || k.includes(locKey.slice(0, 3)));
      const center = getLocationCenter(matchingKey || 'apron');
      const color = SEVERITY_COLORS[incident.severity] || SEVERITY_COLORS.info;
      const brightColor = color === '#3b82f6' ? '#60a5fa' : color;
      const isSelected = selectedIncident?.id === incident.id;
      const offsetX = getStableOffset(incident.id, 25);
      const offsetY = getStableOffset(incident.id + 'y', 12);
      const cx = center.x + offsetX;
      const cy = center.y + offsetY;
      
      // Create marker group
      const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      g.setAttribute('class', 'facility-marker');
      g.style.cursor = 'pointer';
      g.onclick = () => onSelectIncident(incident);
      
      // Background circle
      const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      bgCircle.setAttribute('cx', cx);
      bgCircle.setAttribute('cy', cy);
      bgCircle.setAttribute('r', isSelected ? 12 : 9);
      bgCircle.setAttribute('fill', '#000000');
      bgCircle.setAttribute('opacity', '0.7');
      g.appendChild(bgCircle);
      
      // White circle
      const whiteCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      whiteCircle.setAttribute('cx', cx);
      whiteCircle.setAttribute('cy', cy);
      whiteCircle.setAttribute('r', isSelected ? 10 : 7);
      whiteCircle.setAttribute('fill', '#ffffff');
      g.appendChild(whiteCircle);
      
      // Main colored circle
      const mainCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      mainCircle.setAttribute('cx', cx);
      mainCircle.setAttribute('cy', cy);
      mainCircle.setAttribute('r', isSelected ? 8 : 5);
      mainCircle.setAttribute('fill', brightColor);
      if (isSelected) {
        mainCircle.setAttribute('stroke', '#ffffff');
        mainCircle.setAttribute('stroke-width', '1.5');
      }
      g.appendChild(mainCircle);
      
      // Incident number text
      const reportNum = incident.report_number?.replace('INC-', '') || String(index + 1);
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', cx);
      text.setAttribute('y', cy + 3);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', isSelected ? '7' : '5');
      text.setAttribute('font-weight', '700');
      text.setAttribute('fill', '#ffffff');
      text.textContent = reportNum.slice(-2);
      g.appendChild(text);
      
      svgRef.current.appendChild(g);
    });
  }, [incidents, selectedIncident, newIncidentIds, onSelectIncident]);

  return (
    <svg 
      ref={svgRef}
      viewBox="0 0 600 310"
      className="w-full h-full bg-slate-950"
    >
      <defs>
        <filter id="facilityGlow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <filter id="pulseGlow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Background */}
      <rect x="0" y="0" width="600" height="310" fill="#0f172a" />

      {/* Grid lines - vertical */}
      <line x1="0" y1="0" x2="0" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="50" y1="0" x2="50" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="100" y1="0" x2="100" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="150" y1="0" x2="150" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="200" y1="0" x2="200" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="250" y1="0" x2="250" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="300" y1="0" x2="300" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="350" y1="0" x2="350" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="400" y1="0" x2="400" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="450" y1="0" x2="450" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="500" y1="0" x2="500" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="550" y1="0" x2="550" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="600" y1="0" x2="600" y2="310" stroke="#1e293b" strokeWidth="0.5" />
      
      {/* Grid lines - horizontal */}
      <line x1="0" y1="0" x2="600" y2="0" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="0" y1="50" x2="600" y2="50" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="0" y1="100" x2="600" y2="100" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="0" y1="150" x2="600" y2="150" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="0" y1="200" x2="600" y2="200" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="0" y1="250" x2="600" y2="250" stroke="#1e293b" strokeWidth="0.5" />
      <line x1="0" y1="300" x2="600" y2="300" stroke="#1e293b" strokeWidth="0.5" />

      {/* Buildings - ATC Tower */}
      <rect x="30" y="30" width="60" height="50" fill="#0ea5e9" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="60" y="59" fontSize="10" fill="#ffffff" textAnchor="middle" fontWeight="600">ATC Tower</text>
      
      {/* Buildings - Terminal */}
      <rect x="200" y="25" width="200" height="60" fill="#64748b" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="300" y="59" fontSize="11" fill="#ffffff" textAnchor="middle" fontWeight="600">Terminal Building</text>
      
      {/* Buildings - Fuel Depot */}
      <rect x="520" y="30" width="70" height="50" fill="#dc2626" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="555" y="59" fontSize="10" fill="#ffffff" textAnchor="middle" fontWeight="600">Fuel Depot</text>
      
      {/* Buildings - Hangar 1 */}
      <rect x="30" y="100" width="90" height="70" fill="#475569" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="75" y="139" fontSize="10" fill="#ffffff" textAnchor="middle" fontWeight="600">Hangar 1</text>
      
      {/* Buildings - Apron */}
      <rect x="150" y="105" width="280" height="60" fill="#334155" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="290" y="139" fontSize="11" fill="#ffffff" textAnchor="middle" fontWeight="600">Apron / Ramp</text>
      
      {/* Buildings - Hangar 2 */}
      <rect x="480" y="100" width="90" height="70" fill="#475569" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="525" y="139" fontSize="10" fill="#ffffff" textAnchor="middle" fontWeight="600">Hangar 2</text>
      
      {/* Taxiway */}
      <rect x="50" y="190" width="500" height="25" fill="#52525b" stroke="#ffffff" strokeWidth="2" rx="4" />
      <text x="300" y="206" fontSize="10" fill="#ffffff" textAnchor="middle" fontWeight="600">Taxiway Alpha</text>
      
      {/* Runway */}
      <rect x="30" y="240" width="540" height="45" fill="#27272a" stroke="#ffffff" strokeWidth="2" rx="2" />
      <text x="300" y="267" fontSize="12" fill="#ffffff" textAnchor="middle" fontWeight="600">Runway 27L / 09R</text>

      {/* Runway markings */}
      <rect x="80" y="257" width="35" height="6" fill="#fbbf24" rx="1" />
      <rect x="170" y="257" width="35" height="6" fill="#fbbf24" rx="1" />
      <rect x="260" y="257" width="35" height="6" fill="#fbbf24" rx="1" />
      <rect x="350" y="257" width="35" height="6" fill="#fbbf24" rx="1" />
      <rect x="440" y="257" width="35" height="6" fill="#fbbf24" rx="1" />
      <rect x="520" y="257" width="35" height="6" fill="#fbbf24" rx="1" />

      {/* Runway center line */}
      <line x1="30" y1="262" x2="570" y2="262" stroke="#fbbf24" strokeWidth="2" strokeDasharray="15,10" />

      {/* Heatmap overlays */}
      {showHeatmap && (
        <>
          <circle cx={300} cy={55} r={60} fill="#ef4444" opacity={0.15} />
          <circle cx={60} cy={135} r={50} fill="#f97316" opacity={0.15} />
          <circle cx={300} cy={262} r={45} fill="#eab308" opacity={0.12} />
        </>
      )}

      {/* Incident markers are rendered via useEffect */}
    </svg>
  );
}

// ============== SVG COMPONENT SYSTEMS ==============
function ComponentsSVG({ components, selectedComponent, onSelectComponent }) {
  const positions = {
    hydraulics: { x: 150, y: 150 },
    electrical: { x: 350, y: 100 },
    avionics: { x: 550, y: 150 },
    fuel: { x: 250, y: 250 },
    engines: { x: 450, y: 250 },
    landing_gear: { x: 650, y: 250 },
    flight_controls: { x: 750, y: 150 },
    pneumatics: { x: 150, y: 350 },
    apu: { x: 350, y: 350 },
    ecs: { x: 550, y: 350 },
  };

  const getColor = (status) => {
    const colors = {
      healthy: '#22c55e',
      warning: '#eab308',
      critical: '#ef4444',
      maintenance: '#f97316',
    };
    return colors[status?.toLowerCase()] || '#6b7280';
  };

  return (
    <svg viewBox="0 0 900 450" className="w-full h-full bg-slate-950">
      <defs>
        <filter id="compGlow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Grid */}
      <pattern id="compGrid" width="50" height="50" patternUnits="userSpaceOnUse">
        <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#1e293b" strokeWidth="0.5" />
      </pattern>
      <rect width="900" height="450" fill="url(#compGrid)" />

      {/* Central system outline */}
      <rect x="200" y="80" width="500" height="300" fill="none" stroke="#334155" strokeWidth="2" strokeDasharray="10,5" rx="10" />
      <text x="450" y="60" fontSize="14" fill="#64748b" textAnchor="middle">Aircraft Systems Overview</text>

      {/* Connection lines */}
      {components.map((comp) => {
        const pos = positions[comp.system?.toLowerCase()] || { x: 450, y: 200 };
        return (
          <line 
            key={`line-${comp.id}`}
            x1="450" y1="200" 
            x2={pos.x} y2={pos.y} 
            stroke="#334155" 
            strokeWidth="1" 
          />
        );
      })}

      {/* Central hub */}
      <circle cx="450" cy="200" r="30" fill="#1e293b" stroke="#0ea5e9" strokeWidth="2" />
      <text x="450" y="205" fontSize="10" fill="#0ea5e9" textAnchor="middle">MAIN BUS</text>

      {/* Component nodes */}
      {components.map((comp) => {
        const pos = positions[comp.system?.toLowerCase()] || { x: 450, y: 200 };
        const color = getColor(comp.health_status);
        const isSelected = selectedComponent?.id === comp.id;

        return (
          <g
            key={comp.id}
            onClick={() => onSelectComponent(comp)}
            style={{ cursor: 'pointer' }}
            transform={`translate(${pos.x}, ${pos.y})`}
          >
            {/* Outer glow for selected */}
            {isSelected && (
              <circle r="45" fill={color} opacity={0.2} filter="url(#compGlow)">
                <animate attributeName="r" values="45;50;45" dur="1s" repeatCount="indefinite" />
              </circle>
            )}
            
            {/* Main circle */}
            <circle
              r="35"
              fill="#1e293b"
              stroke={color}
              strokeWidth={isSelected ? 3 : 2}
            />
            
            {/* Health indicator arc */}
            <circle
              r="28"
              fill="none"
              stroke={color}
              strokeWidth="4"
              strokeDasharray={`${comp.health_score * 1.76} 176`}
              transform="rotate(-90)"
              opacity={0.8}
            />
            
            {/* Score text */}
            <text y="5" fontSize="12" fill="#fff" textAnchor="middle" fontWeight="bold">
              {comp.health_score}%
            </text>
            
            {/* Label */}
            <text y="55" fontSize="10" fill="#94a3b8" textAnchor="middle">
              {comp.name.split(' ').slice(0, 2).join(' ')}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ============== MAIN PAGE ==============
export default function DigitalTwinPage({ onNavigate }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('aircraft');
  const [incidents, setIncidents] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showHotspots, setShowHotspots] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [newIncidentIds, setNewIncidentIds] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  const [relatedData, setRelatedData] = useState({ aircraft: null, engine: null, component: null });
  const previousIncidentIdsRef = useRef(new Set());

  const token = localStorage.getItem('token');

  // Initial data load
  useEffect(() => {
    fetchData();
  }, []);

  // Real-time polling for new incidents (every 30 seconds)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      pollForNewIncidents();
    }, 30000); // 30 seconds

    return () => clearInterval(pollInterval);
  }, [token]);

  // Clear "new" status after 10 seconds
  useEffect(() => {
    if (newIncidentIds.length > 0) {
      const timeout = setTimeout(() => {
        setNewIncidentIds([]);
      }, 10000); // Clear after 10 seconds
      return () => clearTimeout(timeout);
    }
  }, [newIncidentIds]);

  const pollForNewIncidents = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/incidents?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const incidentsList = data.incidents || data || [];
        
        // Find new incidents by comparing IDs
        const currentIds = new Set(incidentsList.map(inc => inc.id));
        const newIds = incidentsList
          .filter(inc => !previousIncidentIdsRef.current.has(inc.id))
          .map(inc => inc.id);
        
        if (newIds.length > 0 && previousIncidentIdsRef.current.size > 0) {
          // New incidents detected!
          setNewIncidentIds(newIds);
          toast.info(`${newIds.length} new incident${newIds.length > 1 ? 's' : ''} reported!`, {
            duration: 5000,
            action: {
              label: 'View',
              onClick: () => {
                const newIncident = incidentsList.find(inc => inc.id === newIds[0]);
                if (newIncident) setSelectedIncident(newIncident);
              }
            }
          });
        }
        
        // Update previous IDs reference
        previousIncidentIdsRef.current = currentIds;
        
        // Update incidents with zones
        const zones = ['cockpit', 'cabin_front', 'cabin_mid', 'cabin_rear', 'wing_left', 'wing_right', 'engine_left', 'engine_right', 'tail', 'landing_gear', 'cargo'];
        const locations = ['terminal', 'hangar_1', 'hangar_2', 'runway', 'taxiway_a', 'taxiway_b', 'apron', 'fuel_depot'];
        
        const incidentsWithZones = incidentsList.map(inc => ({
          ...inc,
          location_zone: inc.location_zone || zones[Math.floor(Math.random() * zones.length)],
          location: inc.location || locations[Math.floor(Math.random() * locations.length)]
        }));
        
        setIncidents(incidentsWithZones);
        setLastFetchTime(new Date());
      }
    } catch (error) {
      console.error('[DigitalTwin] Polling error:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const incRes = await fetch(`${API_URL}/api/incidents?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (incRes.ok) {
        const data = await incRes.json();
        const zones = ['cockpit', 'cabin_front', 'cabin_mid', 'cabin_rear', 'wing_left', 'wing_right', 'engine_left', 'engine_right', 'tail', 'landing_gear', 'cargo'];
        const locations = ['terminal', 'hangar_1', 'hangar_2', 'runway', 'taxiway_a', 'taxiway_b', 'apron', 'fuel_depot'];
        
        const incidentsList = data.incidents || data || [];
        const incidentsWithZones = incidentsList.map(inc => ({
          ...inc,
          location_zone: inc.location_zone || zones[Math.floor(Math.random() * zones.length)],
          location: inc.location || locations[Math.floor(Math.random() * locations.length)]
        }));
        
        // Initialize previousIncidentIdsRef on first load
        previousIncidentIdsRef.current = new Set(incidentsList.map(inc => inc.id));
        
        setIncidents(incidentsWithZones);
        setLastFetchTime(new Date());
      }

      setComponents([
        { id: '1', name: 'Hydraulic System A', system: 'hydraulics', health_status: 'healthy', health_score: 95 },
        { id: '2', name: 'Main Electrical Bus', system: 'electrical', health_status: 'healthy', health_score: 98 },
        { id: '3', name: 'Flight Management', system: 'avionics', health_status: 'warning', health_score: 78 },
        { id: '4', name: 'Center Fuel Tank', system: 'fuel', health_status: 'healthy', health_score: 92 },
        { id: '5', name: 'Engine #1', system: 'engines', health_status: 'maintenance', health_score: 85 },
        { id: '6', name: 'Landing Gear', system: 'landing_gear', health_status: 'healthy', health_score: 90 },
        { id: '7', name: 'Flight Controls', system: 'flight_controls', health_status: 'healthy', health_score: 96 },
        { id: '8', name: 'Pneumatic Pack', system: 'pneumatics', health_status: 'critical', health_score: 45 },
        { id: '9', name: 'APU Generator', system: 'apu', health_status: 'warning', health_score: 72 },
        { id: '10', name: 'Environmental', system: 'ecs', health_status: 'healthy', health_score: 88 },
      ]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredIncidents = incidents.filter(inc => 
    severityFilter === 'all' || inc.severity === severityFilter
  );

  const getComponentColor = (status) => {
    const colors = { healthy: '#22c55e', warning: '#eab308', critical: '#ef4444', maintenance: '#f97316' };
    return colors[status?.toLowerCase()] || '#6b7280';
  };

  // Fetch related aircraft/engine/component data when incident is selected
  useEffect(() => {
    const fetchRelatedData = async () => {
      if (!selectedIncident || !token) {
        setRelatedData({ aircraft: null, engine: null, component: null });
        return;
      }

      const newRelatedData = { aircraft: null, engine: null, component: null };

      try {
        // Fetch aircraft details if aircraft_id exists
        if (selectedIncident.aircraft_id) {
          const aircraftRes = await fetch(`${API_URL}/api/aircraft/${selectedIncident.aircraft_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (aircraftRes.ok) {
            newRelatedData.aircraft = await aircraftRes.json();
          }
        }

        // Fetch engine details if engine_id exists
        if (selectedIncident.engine_id) {
          const engineRes = await fetch(`${API_URL}/api/engines/${selectedIncident.engine_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (engineRes.ok) {
            newRelatedData.engine = await engineRes.json();
          }
        }

        // Fetch component details if component_id exists
        if (selectedIncident.component_id) {
          const componentRes = await fetch(`${API_URL}/api/components/${selectedIncident.component_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (componentRes.ok) {
            newRelatedData.component = await componentRes.json();
          }
        }
      } catch (error) {
        console.error('Error fetching related data:', error);
      }

      setRelatedData(newRelatedData);
    };

    fetchRelatedData();
  }, [selectedIncident, token]);

  // Handle incident click - show modal with details
  const handleIncidentClick = (incident) => {
    setSelectedIncident(incident);
    setIncidentModalOpen(true);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="digital-twin-page">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-800">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Layers className="h-6 w-6 text-sky-500" />
            {t('digitalTwin.title')}
            <Badge variant="outline" className="ml-2 text-emerald-400 border-emerald-400">2D Interactive</Badge>
            {newIncidentIds.length > 0 && (
              <Badge className="ml-2 bg-red-500 text-white animate-pulse">
                {newIncidentIds.length} NEW
              </Badge>
            )}
          </h1>
          <p className="text-gray-400 text-sm">
            {t('digitalTwin.interactiveVisualization')}
            {lastFetchTime && (
              <span className="ml-2 text-gray-500">
                • {t('digitalTwin.lastUpdated')}: {lastFetchTime.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setHelpOpen(!helpOpen)}>
            <HelpCircle className="h-4 w-4 mr-1" /> {t('common.help')}
            {helpOpen ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-1" /> {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Collapsible Help Section */}
      <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
        <CollapsibleContent>
          <div className="px-4 py-3 bg-slate-800/50 border-b border-slate-700">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold text-sky-400 mb-2 flex items-center gap-1">
                    <Plane className="h-4 w-4" /> {t('digitalTwin.aircraftView')}
                  </h4>
                  <p className="text-gray-400">
                    {t('digitalTwin.aircraftViewDesc')}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-sky-400 mb-2 flex items-center gap-1">
                    <Building2 className="h-4 w-4" /> {t('digitalTwin.facilityView')}
                  </h4>
                  <p className="text-gray-400">
                    {t('digitalTwin.facilityViewDesc')}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-sky-400 mb-2 flex items-center gap-1">
                    <Cpu className="h-4 w-4" /> {t('digitalTwin.systemsView')}
                  </h4>
                  <p className="text-gray-400">
                    {t('digitalTwin.systemsViewDesc')}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-gray-500">
                <span className="font-medium text-gray-400">{t('digitalTwin.tip')}:</span> {t('digitalTwin.tipText')}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      <div className="flex-1 flex overflow-hidden">
        {/* Visualization Area */}
        <div className="flex-1 bg-slate-950 h-full">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <div className="px-4 pt-2 border-b border-slate-800 flex-shrink-0">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="aircraft" data-testid="tab-aircraft">
                  <Plane className="h-4 w-4 mr-2" /> {t('digitalTwin.aircraft')}
                </TabsTrigger>
                <TabsTrigger value="facility" data-testid="tab-facility">
                  <Building2 className="h-4 w-4 mr-2" /> {t('digitalTwin.facility')}
                </TabsTrigger>
                <TabsTrigger value="components" data-testid="tab-components">
                  <Cpu className="h-4 w-4 mr-2" /> {t('digitalTwin.systems')}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden relative">
              <TabsContent value="aircraft" className="absolute inset-0 m-0 p-4">
                <div className="w-full h-full flex items-center justify-center">
                  <AircraftSVG 
                    incidents={filteredIncidents}
                    selectedIncident={selectedIncident}
                    onSelectIncident={handleIncidentClick}
                    showHotspots={showHotspots}
                    newIncidentIds={newIncidentIds}
                  />
                </div>
              </TabsContent>

              <TabsContent value="facility" className="absolute inset-0 m-0 p-4">
                <div className="w-full h-full bg-slate-950 rounded-lg">
                  <FacilitySVG 
                    incidents={filteredIncidents}
                    selectedIncident={selectedIncident}
                    onSelectIncident={handleIncidentClick}
                    showHeatmap={showHeatmap}
                    newIncidentIds={newIncidentIds}
                  />
                </div>
              </TabsContent>

              <TabsContent value="components" className="absolute inset-0 m-0 p-4">
                <div className="w-full h-full flex items-center justify-center">
                  <ComponentsSVG 
                    components={components}
                    selectedComponent={selectedComponent}
                    onSelectComponent={setSelectedComponent}
                  />
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Side Panel */}
        <div className="w-72 border-l border-slate-800 bg-slate-900/50 flex flex-col">
          <div className="p-4 border-b border-slate-800">
            <h3 className="text-white font-semibold mb-3">{t('digitalTwin.controls')}</h3>
            
            {activeTab !== 'components' && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-gray-400">{t('digitalTwin.showHotspots')}</Label>
                  <Switch checked={showHotspots} onCheckedChange={setShowHotspots} />
                </div>
                
                {activeTab === 'facility' && (
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-gray-400">{t('digitalTwin.showHeatmap')}</Label>
                    <Switch checked={showHeatmap} onCheckedChange={setShowHeatmap} />
                  </div>
                )}
                
                <div className="mb-3">
                  <Label className="text-gray-400 text-sm">{t('digitalTwin.severityFilter')}</Label>
                  <Select value={severityFilter} onValueChange={setSeverityFilter}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('common.all')}</SelectItem>
                      <SelectItem value="critical">{t('common.critical')}</SelectItem>
                      <SelectItem value="major">{t('incidents.severities.major')}</SelectItem>
                      <SelectItem value="minor">{t('incidents.severities.minor')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <ScrollArea className="flex-1">
            <div className="p-4">
              {activeTab === 'components' ? (
                <>
                  <h4 className="text-gray-400 text-sm mb-2">{t('digitalTwin.components')}</h4>
                  <div className="space-y-2">
                    {components.map(comp => (
                      <div
                        key={comp.id}
                        className={`p-2 rounded cursor-pointer ${selectedComponent?.id === comp.id ? 'bg-sky-900/50 border border-sky-700' : 'bg-slate-800/50 hover:bg-slate-800'}`}
                        onClick={() => setSelectedComponent(comp)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm">{comp.name}</span>
                          <Badge className="text-xs" style={{ backgroundColor: getComponentColor(comp.health_status) }}>
                            {comp.health_score}%
                          </Badge>
                        </div>
                        <p className="text-gray-500 text-xs">{comp.system}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <h4 className="text-gray-400 text-sm mb-2">{t('common.incidents')} ({filteredIncidents.length})</h4>
                  <div className="space-y-2">
                    {filteredIncidents.slice(0, 15).map(incident => (
                      <div
                        key={incident.id}
                        className={`p-2 rounded cursor-pointer ${selectedIncident?.id === incident.id ? 'bg-sky-900/50 border border-sky-700' : 'bg-slate-800/50 hover:bg-slate-800'}`}
                        onClick={() => handleIncidentClick(incident)}
                      >
                        <div className="flex items-start justify-between">
                          <p className="text-white text-sm truncate flex-1">{incident.title}</p>
                          <Badge className="ml-1 text-xs" style={{ backgroundColor: SEVERITY_COLORS[incident.severity] || SEVERITY_COLORS.info }}>
                            {incident.severity?.slice(0, 3)}
                          </Badge>
                        </div>
                        <p className="text-gray-500 text-xs">{incident.report_number}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Legend */}
          <div className="p-4 border-t border-slate-800">
            <h4 className="text-gray-400 text-sm mb-2">{t('digitalTwin.legend')}</h4>
            <div className="grid grid-cols-2 gap-1 text-xs">
              {Object.entries(activeTab === 'components' 
                ? { healthy: '#22c55e', warning: '#eab308', critical: '#ef4444', maintenance: '#f97316' }
                : { critical: '#ef4444', major: '#f97316', minor: '#22c55e', info: '#3b82f6' }
              ).map(([key, color]) => (
                <div key={key} className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                  <span className="text-gray-400 capitalize">{key}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Incident Detail Modal */}
      <Dialog open={incidentModalOpen} onOpenChange={setIncidentModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t('digitalTwin.incidentDetails')}
            </DialogTitle>
            {selectedIncident && (
              <DialogDescription className="text-slate-400">
                {selectedIncident.report_number}
              </DialogDescription>
            )}
          </DialogHeader>
          
          {selectedIncident && (
            <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              <div className="space-y-5">
                {/* Title and Severity */}
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-white font-semibold text-lg">{selectedIncident.title}</h3>
                  <Badge 
                    className="text-xs flex-shrink-0" 
                    style={{ backgroundColor: SEVERITY_COLORS[selectedIncident.severity] || SEVERITY_COLORS.info }}
                  >
                    {selectedIncident.severity?.toUpperCase()}
                  </Badge>
                </div>

                {/* Key Details Grid */}
                <div className="grid grid-cols-2 gap-4 text-sm bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 text-slate-300">
                    <Calendar className="h-5 w-5 text-sky-400" />
                    <div>
                      <p className="text-xs text-slate-500">Date & Time</p>
                      <p>{formatDate(selectedIncident.incident_date || selectedIncident.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <MapPin className="h-5 w-5 text-sky-400" />
                    <div>
                      <p className="text-xs text-slate-500">Location</p>
                      <p>{selectedIncident.location || selectedIncident.location_zone || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <FileText className="h-5 w-5 text-sky-400" />
                    <div>
                      <p className="text-xs text-slate-500">Category</p>
                      <p className="capitalize">{selectedIncident.category || selectedIncident.incident_type || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Activity className="h-5 w-5 text-sky-400" />
                    <div>
                      <p className="text-xs text-slate-500">Status</p>
                      <p className="capitalize">{selectedIncident.status || 'Open'}</p>
                    </div>
                  </div>
                </div>

                {/* Description Section */}
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Description
                  </h4>
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">
                    {selectedIncident.description || 'No description provided.'}
                  </p>
                </div>

                {/* Aircraft & Flight Information - Always show */}
                <div className="bg-slate-800/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                    <Plane className="h-4 w-4" />
                    Aircraft & Flight Information
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {/* Aircraft details from related data or incident */}
                    <div>
                      <p className="text-xs text-slate-500">Aircraft Registration</p>
                      <p className="text-white font-medium">
                        {relatedData.aircraft?.registration || selectedIncident.aircraft_registration || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Aircraft Type</p>
                      <p className="text-white font-medium">
                        {relatedData.aircraft?.aircraft_type || selectedIncident.aircraft_type || 'N/A'}
                      </p>
                    </div>
                    {relatedData.aircraft && (
                      <>
                        <div>
                          <p className="text-xs text-slate-500">Aircraft Number</p>
                          <p className="text-white font-medium">{relatedData.aircraft.aircraft_number || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Serial Number</p>
                          <p className="text-white font-medium">{relatedData.aircraft.serial_number || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Operator</p>
                          <p className="text-white font-medium">{relatedData.aircraft.operator_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Base Airport</p>
                          <p className="text-white font-medium">{relatedData.aircraft.base_airport || 'N/A'}</p>
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-xs text-slate-500">Flight Number</p>
                      <p className="text-white font-medium">{selectedIncident.flight_number || 'N/A'}</p>
                    </div>
                    {selectedIncident.flight_phase && (
                      <div>
                        <p className="text-xs text-slate-500">Flight Phase</p>
                        <p className="text-white font-medium capitalize">{selectedIncident.flight_phase}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Engine Information - Show if engine data exists */}
                {relatedData.engine && (
                  <div className="bg-slate-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                      <Cpu className="h-4 w-4" />
                      Engine Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Engine Model</p>
                        <p className="text-white font-medium">{relatedData.engine.model || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Serial Number</p>
                        <p className="text-white font-medium">{relatedData.engine.serial_number || 'N/A'}</p>
                      </div>
                      {relatedData.engine.manufacturer && (
                        <div>
                          <p className="text-xs text-slate-500">Manufacturer</p>
                          <p className="text-white font-medium">{relatedData.engine.manufacturer}</p>
                        </div>
                      )}
                      {relatedData.engine.position && (
                        <div>
                          <p className="text-xs text-slate-500">Position</p>
                          <p className="text-white font-medium">{relatedData.engine.position}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Component Information - Show if component data exists */}
                {relatedData.component && (
                  <div className="bg-slate-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Component Information
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Part Number</p>
                        <p className="text-white font-medium">{relatedData.component.part_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Serial Number</p>
                        <p className="text-white font-medium">{relatedData.component.serial_number || 'N/A'}</p>
                      </div>
                      {relatedData.component.name && (
                        <div>
                          <p className="text-xs text-slate-500">Component Name</p>
                          <p className="text-white font-medium">{relatedData.component.name}</p>
                        </div>
                      )}
                      {relatedData.component.description && (
                        <div className="col-span-2">
                          <p className="text-xs text-slate-500">Description</p>
                          <p className="text-white font-medium">{relatedData.component.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Additional Details */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Reporter Information */}
                  <div className="bg-slate-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Reporter
                    </h4>
                    <div className="space-y-1 text-sm">
                      <p className="text-slate-300">
                        {selectedIncident.reporter_name || (selectedIncident.is_anonymous ? 'Anonymous' : 'Unknown')}
                      </p>
                      {selectedIncident.reporter_department && (
                        <p className="text-slate-400 text-xs">{selectedIncident.reporter_department}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Risk Assessment */}
                {(selectedIncident.risk_score || selectedIncident.risk_level) && (
                  <div className="bg-slate-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Risk Assessment
                    </h4>
                    <div className="flex items-center gap-4">
                      {selectedIncident.risk_score && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-white">{selectedIncident.risk_score}</p>
                          <p className="text-xs text-slate-400">Risk Score</p>
                        </div>
                      )}
                      {selectedIncident.risk_level && (
                        <Badge 
                          className="text-xs" 
                          style={{ 
                            backgroundColor: selectedIncident.risk_level === 'high' ? '#ef4444' : 
                                           selectedIncident.risk_level === 'medium' ? '#f97316' : '#22c55e' 
                          }}
                        >
                          {selectedIncident.risk_level?.toUpperCase()} RISK
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Corrective Actions */}
                {selectedIncident.corrective_actions && (
                  <div className="bg-slate-800/30 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      Corrective Actions
                    </h4>
                    <p className="text-slate-300 text-sm whitespace-pre-wrap">
                      {selectedIncident.corrective_actions}
                    </p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-700">
                  <span>Created: {formatDate(selectedIncident.created_at)}</span>
                  {selectedIncident.updated_at && selectedIncident.updated_at !== selectedIncident.created_at && (
                    <span>Updated: {formatDate(selectedIncident.updated_at)}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-shrink-0 mt-4 pt-4 border-t border-slate-700">
            <Button 
              className="w-full bg-sky-600 hover:bg-sky-700 text-white"
              onClick={() => setIncidentModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
